/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { createPortal } from 'react-dom'
import { View, Pressable, Icon } from '@components/atoms'
import { BackofficeTheme } from '@components/systems/backoffice/BackofficeTheme'
import {
	mono,
	boFont,
	boSize,
	boMedia,
} from '@components/systems/backoffice/backoffice.tokens'

import { useNavigate } from 'react-router-dom'

interface PropsType {
	title: string
	customTitle?: JSX.Element
	className?: string
	onCloseClick?: () => void
	size?: 'small' | 'middle' | 'large'
	titleStyle?: React.CSSProperties
	children?: React.ReactNode
	showCloseButton?: boolean
	/** 닫기 글자. 고객 화면(영어)에서 'Close' 로 바꾼다. 기본 '닫기' */
	closeLabel?: string
	leftSideContent?: JSX.Element
	/**
	 * `true` 면 라우트가 아니라 **페이지 위에 덮이는 모달**로 동작한다 — 닫기는 `onCloseClick` 만 부르고
	 * 뒤로가기를 하지 않는다. 쉘(사이드바·탭 바) 위, MUI Dialog(확인 모달) 아래에 서도록 z-index 1250 을 걸고 Esc 로 닫힌다.
	 * 기본(false)은 자기 라우트를 가진 전체 화면(마이페이지 등)이다.
	 */
	overlay?: boolean
}

/**
 * 전체 화면 한 장(마이페이지·패스워드 변경·시즌 상품 상세).
 * 홈의 MonolithDeviceModal 과 같은 문법 — 그라운드를 꽉 채우고, 상단 바 48 에 닫기(텍스트 + ×), 본문은 중앙 컬럼.
 */
export const FullModalLayout = (props: PropsType) => {
	// hook
	const navigate = useNavigate()

	// variables
	const size: 'small' | 'middle' | 'large' = props.size ?? 'middle'
	const contentMaxWidth: number = size === 'small' ? 560 : size === 'middle' ? 760 : 1200
	const showCloseButton: boolean = props.showCloseButton ?? true
	const overlay: boolean = props.overlay ?? false

	const onBackClick = React.useCallback(() => {
		if (props.onCloseClick !== undefined) {
			props.onCloseClick()
		}

		if (!overlay) {
			navigate(-1)
		}
	}, [props.onCloseClick, overlay])

	// 오버레이 — Esc 로 닫힌다
	React.useEffect(() => {
		if (!overlay) return
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onBackClick()
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [overlay, onBackClick])

	const node = (
		<BackofficeTheme>
			<View css={style}>
				<View
					className="fullModalView"
					role={overlay ? 'dialog' : undefined}
					aria-modal={overlay ? true : undefined}
					css={overlay ? { zIndex: 1250 } : undefined}
				>
					<View className="topBar">
						<View className="topBarLeft">{props.leftSideContent ?? null}</View>
						{showCloseButton && (
							<Pressable className="closeButton" onClick={onBackClick}>
								<span className="closeLabel">{props.closeLabel ?? '닫기'}</span>
								<Icon name="close" className="closeIcon" />
							</Pressable>
						)}
					</View>

					<View className="contentView">
						<View
							className={`contentInnerView ${props.className ?? ''}`}
							css={{ maxWidth: contentMaxWidth }}
						>
							<View className="titleView">
								{props.customTitle ? (
									props.customTitle
								) : (
									<h1 className="titleText" css={{ ...props.titleStyle }}>
										{props.title}
									</h1>
								)}
							</View>

							{props.children}
						</View>
					</View>
				</View>
			</View>
		</BackofficeTheme>
	)

	// 오버레이는 body 에 붙인다 — 그리드·레이아웃 안쪽 스택 컨텍스트에 갇히면 사이드바·탭 바 아래로 깔린다
	return overlay ? createPortal(node, document.body) : node
}

const style = css({
	fontFamily: boFont.ui,
	color: mono.ink,
	textAlign: 'left',

	'& .text': { fontFamily: boFont.ui },

	'.fullModalView': {
		position: 'fixed',
		inset: 0,
		width: '100vw',
		height: ['100vh', '100dvh'],
		display: 'flex',
		flexDirection: 'column',
		background: mono.bg,
		overflow: 'hidden',

		'.topBar': {
			flexShrink: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			height: boSize.navH,
			padding: `0 ${boSize.gutter}`,
			borderBottom: `1px solid ${mono.line}`,

			'.topBarLeft': { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },

			'.closeButton': {
				display: 'flex',
				alignItems: 'center',
				gap: 6,
				height: 32,
				padding: '0 4px 0 10px',
				borderRadius: 2,
				color: mono.dim,
				transition: 'color 200ms ease-out',

				'.closeLabel': { fontSize: 13, fontWeight: 500 },
				'.closeIcon': { fontSize: 18, color: 'inherit' },
				'&:hover': { color: mono.ink },
			},
		},

		'.contentView': {
			flex: 1,
			minHeight: 0,
			overflow: 'auto',
			// 세로만 가둔다 — 가로를 가두면 가장자리 스와이프 뒤로가기가 죽는다 (DESIGN_SYSTEM §10)
			overscrollBehaviorY: 'contain',
			overscrollBehaviorX: 'auto',
			WebkitOverflowScrolling: 'touch',
			padding: `0 ${boSize.gutter}`,

			'.contentInnerView': {
				position: 'relative',
				width: '100%',
				margin: '0 auto',
				paddingBottom: 80,

				'.titleView': {
					display: 'flex',
					justifyContent: 'flex-start',
					alignItems: 'center',
					padding: 'clamp(32px, 5vw, 64px) 0 24px',

					'.titleText': {
						margin: 0,
						fontSize: 'clamp(28px, 3.2vw, 40px)',
						fontWeight: 200,
						lineHeight: 1.1,
						letterSpacing: '-0.01em',
						color: mono.ink,
					},
				},
			},
		},

		// 폰 (RESPONSIVE_PLAN §6) — 상단 바 safe-area, 여백 16, 제목 축소
		[boMedia.phone]: {
			'.topBar': {
				paddingTop: 'env(safe-area-inset-top, 0px)',
				height: `calc(${boSize.navH}px + env(safe-area-inset-top, 0px))`,
				padding: '0 8px 0 16px',
				'.topBarLeft': { gap: 6, '& button': { minHeight: 44 } },
				'.closeButton': { height: 44, padding: '0 8px 0 10px' },
			},
			'.contentView': {
				padding: '0 16px',
				'.contentInnerView': {
					paddingBottom: 'calc(40px + env(safe-area-inset-bottom, 0px))',
					'.titleView': {
						padding: '20px 0 16px',
						'.titleText': {
							fontSize: 'clamp(24px, 6vw, 32px)',
							overflowWrap: 'anywhere',
						},
					},
				},
			},
		},
	},
})
